import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { finalize, map } from 'rxjs/operators';

import { AuthApiUserService } from './auth-api-user.service';
import { TokenService } from './token.service';
import { AcceptedTermsOfUse } from './model/accepted-terms-of-use.interface';
import { Token } from './model/token.interface';
import { LocalStorageService } from '../../storage/local-storage.service';
import { CleanService } from '../../storage/clean.service';
import { MRARights } from './model/account-rights.interface';
import { DeviceApiService } from '../device/device-api.service';

@Injectable({ providedIn: 'root' })
export class AuthService {

  constructor(private authApiUserService: AuthApiUserService,
              private tokenService: TokenService,
              private localStorageService: LocalStorageService,
              private cleanService: CleanService,
              private router: Router,
              private deviceApiService: DeviceApiService) {
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // Public
  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  login$(user: string, password: string, code: string = null, acceptedTermsOfUse: AcceptedTermsOfUse[] = null): Observable<Token> {
    return this.authApiUserService.login$(user, password, code, acceptedTermsOfUse).pipe(
      map(token => this.handleAuthRequest(token)),
    );
  }

  logout(): void {
    this.authApiUserService.logout$().pipe(
      finalize(() => {
        this.localStorageService.removeItem('baseToken');
        this.cleanService.clean();
        this.router.navigate(['login']);
      }),
    ).subscribe();
  }

  checkAuthentication(): Promise<boolean> {
    return this.tokenService.isBaseTokenValid();
  }

  checkMRARightsForAccountIds$(accountIds: string[]): Observable<MRARights[]> {
    return this.deviceApiService.readAccountsRights$(accountIds).pipe(map(rights => {
      const mraRights: MRARights[] = [];

      rights.forEach(right => {
        if (right.rights.includes('DEVICE_READ') &&
          right.rights.includes('DEVICE_CREATE') &&
          right.rights.includes('DEVICE_UPDATE_IMAGE') &&
          right.rights.includes('SITE_READ')) {
          const mraRight: MRARights = {
            id: right.id,
            rights: true,
          };
          mraRights.push(mraRight);
        } else {
          const mraRight: MRARights = {
            id: right.id,
            rights: false,
          };
          mraRights.push(mraRight);
        }
      });

      return mraRights;
    }));
  }

  checkMRARightsForAccountId$(accountId: string): Observable<MRARights> {
    return this.deviceApiService.readAccountRights$(accountId).pipe(map(rights => {
      const mraRights: MRARights = {
        id: accountId,
        rights: false
      };

      if (rights.includes('DEVICE_READ') &&
          rights.includes('DEVICE_CREATE') &&
          rights.includes('DEVICE_UPDATE_IMAGE') &&
          rights.includes('SITE_READ')) {
        mraRights.rights = true;
      }

      return mraRights;
    }));
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // Private
  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  private handleAuthRequest(responseData: Token): Token {
    if (responseData && responseData.value) {
      this.tokenService.registerBaseSessionToken(responseData);
    } else {
      throw new Error('Unexpected auth request response');
    }
    return responseData;
  }
}
